import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * One venue's AI phone ordering settings (`ai-phone-ordering` flag): the number its AI agent
 * answers. Customers ring it to order; the venue keeps its own number (`restaurants.phone`) for
 * everything else. How a call flows: `packages/backend/convex/aiPhone/README.md`.
 *
 * Super admins set it after importing the number into ElevenLabs. A number belongs to one venue,
 * since the number dialled is how a call finds its venue.
 */
export const aiPhoneSettingsSchema = z.object({
  restaurantId: z.string(),
  /**
   * The number the AI agent answers for this venue, in E.164 (`+61341576921`): never the
   * customer's. ElevenLabs sends calls' `called_number` in this form.
   */
  agentPhoneNumber: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type AiPhoneSettings = z.output<typeof aiPhoneSettingsSchema>;

/** Strict schema */
export const aiPhoneSettingsStrictSchema = aiPhoneSettingsSchema.safeExtend({
  agentPhoneNumber: z.string().regex(/^\+[1-9]\d{7,14}$/, 'Must be a number like +61341576921'),
});

/** Convex schema */
export const aiPhoneSettingsCvxSchema = aiPhoneSettingsSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type AiPhoneSettingsCvx = z.output<typeof aiPhoneSettingsCvxSchema>;
assertTypes<NoNewFields<AiPhoneSettings, AiPhoneSettingsCvx>>();
